/**
 * TextEditingManager - Manages text entity editing operations.
 *
 * Provides API for checking if text can be edited, getting/setting text content
 * on selected text entities (OdDbText and OdDbMText).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import { selectionService } from "../selection";
import type { CommandHistory } from "../commands/CommandHistory";
import type { DrawingModule } from "../types/DrawingWeb";
import { getEntityHandle, findEntityByHandle } from "../core/oda";
import { switchMLeaderStyle } from "../leaders/mleaderStyle";
import { EditTextCommand } from "../commands/EditTextCommand";
import { SetTextHeightCommand } from "../commands/SetTextHeightCommand";
import { SetTextStyleCommand } from "../commands/SetTextStyleCommand";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import {
  isEditableTextEntity,
  getEntityType,
  EntityType,
} from "../entities/detection";
import { recordGraphicsModified } from "../entities";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";
import {
  ensureTextStyleExists,
  applyTextStyleToEntity,
  applyTextStyleToMText,
} from "../database/textStyle";
import type { Textstyle } from "@/types/legend.types";

export class TextEditingManager {
  constructor(
    readonly viewer: DRDrawingViewer,
    private history: CommandHistory,
  ) {}

  /**
   * Check if the selected entity is specifically an MText (OdDbMText).
   * @returns True if exactly one MText entity is selected
   */
  isSelectedMText(): boolean {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return false;
    return (
      getEntityType(this.viewer.module, selectedIds[0]) === EntityType.MText
    );
  }

  /**
   * Check if exactly one text entity is selected for editing.
   * @returns True if exactly one text (OdDbText or OdDbMText) is selected
   */
  canEdit(): boolean {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return false;

    return isEditableTextEntity(this.viewer.module, selectedIds[0]);
  }

  /**
   * Get the text content of the selected text entity.
   * @returns The text content, or null if no single text entity is selected
   */
  getSelectedText(): string | null {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return null;

    const entityId = selectedIds[0];
    if (!isEditableTextEntity(this.viewer.module, entityId)) return null;

    return this.getTextContent(entityId);
  }

  /**
   * Set the text content of the selected text entity.
   * @param newText The new text content
   * @returns True if the text was successfully set
   */
  setSelectedText(newText: string): boolean {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return false;

    const entityId = selectedIds[0];
    if (!isEditableTextEntity(this.viewer.module, entityId)) return false;

    // Get old text for undo
    const oldText = this.getTextContent(entityId);
    if (oldText === null) return false;

    // Set the new text
    const success = this.setTextContent(entityId, newText);
    if (!success) return false;

    noteGraphicsModified(getEntityType(this.viewer.module, entityId));

    // Create command for undo/redo
    trackShapeChange("text_edited");
    const command = new EditTextCommand(this.viewer, {
      entityId,
      oldText,
      newText,
    });
    this.history.execute(command);

    deleteObj(entityId);

    // Update the view
    this.viewer.update("TextEditingManager.setSelectedText");

    // update grid
    selectionService.updateGrid();

    return true;
  }

  /**
   * Get text content from a text entity (OdDbText or OdDbMText).
   */
  private getTextContent(entityId: DrawingModule.OdDbObjectId): string | null {
    const module = this.viewer.module;
    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;
    let content: DrawingModule.OdString | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        text = module.OdDbText.cast(pObj);
        if (text) {
          content = text.textString();
          return content.toString();
        }
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) {
          content = mtext.contents();
          return content.toString();
        }
      } else if (entityType === EntityType.MLeader) {
        entity = entityId.safeOpenObject(OpenMode.kForRead, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) {
          mtext = mleader.mtext();
          if (mtext) {
            content = mtext.contents();
            return content.toString();
          }
        }
      }

      return null;
    } finally {
      if (content) deleteObj(content);
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
    }
  }

  /**
   * Set text content on a text entity (OdDbText or OdDbMText).
   */
  private setTextContent(
    entityId: DrawingModule.OdDbObjectId,
    newText: string,
  ): boolean {
    const module = this.viewer.module;
    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;
    let textStr: DrawingModule.OdString | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        text = module.OdDbText.cast(pObj);
        if (text) {
          textStr = new module.OdString(newText);
          text.setTextString(textStr);
          return true;
        }
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) {
          textStr = new module.OdString(newText);
          mtext.setContents(textStr);
          return true;
        }
      } else if (entityType === EntityType.MLeader) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mleader = module.OdDbMLeader.cast(pObj);
        if (mleader) {
          mtext = mleader.mtext();
          if (mtext) {
            textStr = new module.OdString(newText);
            mtext.setContents(textStr);
            mleader.setMText(mtext);
          }
          return true;
        }
      }

      return false;
    } finally {
      if (textStr) deleteObj(textStr);
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (pObj) deleteObj(pObj);
    }
  }

  /**
   * Get the text height of the selected text entity.
   * @returns The text height in world units, or null if no single text entity is selected
   */
  getSelectedTextHeight(): number | null {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return null;

    const entityId = selectedIds[0];
    if (!isEditableTextEntity(this.viewer.module, entityId)) return null;

    return this.getTextHeight(entityId);
  }

  /**
   * Set the text height of the selected text entity.
   * @param height The new text height in world units
   * @returns True if the text height was successfully set
   */
  setSelectedTextHeight(height: number): boolean {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return false;

    const entityId = selectedIds[0];
    if (!isEditableTextEntity(this.viewer.module, entityId)) return false;

    const oldHeight = this.getTextHeight(entityId);
    const success = this.setTextHeight(entityId, height);
    if (success) {
      if (oldHeight !== null) {
        const command = new SetTextHeightCommand(this, {
          handle: getEntityHandle(entityId),
          oldHeight,
          newHeight: height,
        });
        this.history.execute(command);
      }
      this.viewer.update();
      selectionService.updateGrid();
    }
    return success;
  }

  /**
   * Set the text style of the selected text entity.
   * Applies the style font and, if present, the style's size as height.
   * @param style The text style to apply
   * @returns True if the text style was successfully set
   */
  setSelectedTextStyle(style: Textstyle): boolean {
    const selectedIds = selectionService.getAll();
    if (selectedIds.length !== 1) return false;

    const entityId = selectedIds[0];
    if (!isEditableTextEntity(this.viewer.module, entityId)) return false;

    const oldHeight = this.getTextHeight(entityId);
    const oldStyleHandle = this.getTextStyleHandle(entityId);
    const success = this.setTextStyle(entityId, style);
    if (success) {
      const newStyleHandle = this.getTextStyleHandle(entityId);
      const newHeight = style.size && style.size > 0 ? style.size : null;
      const command = new SetTextStyleCommand(this, {
        handle: getEntityHandle(entityId),
        oldStyleHandle,
        oldHeight,
        newStyleHandle: newStyleHandle!,
        newHeight,
      });
      this.history.execute(command);
      this.viewer.update();
      selectionService.updateGrid();
    }
    return success;
  }

  /**
   * Get text height from a text entity (OdDbText or OdDbMText).
   */
  private getTextHeight(entityId: DrawingModule.OdDbObjectId): number | null {
    const module = this.viewer.module;
    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        text = module.OdDbText.cast(pObj);
        if (text) return text.height();
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) return mtext.textHeight();
      } else if (entityType === EntityType.MLeader) {
        entity = entityId.safeOpenObject(OpenMode.kForRead, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) return mleader.textHeight();
      }

      return null;
    } finally {
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
    }
  }

  private getTextStyleHandle(
    entityId: DrawingModule.OdDbObjectId,
  ): string | null {
    const module = this.viewer.module;
    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;
    let styleId: DrawingModule.OdDbObjectId | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        text = module.OdDbText.cast(pObj);
        if (text) styleId = text.textStyle();
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) styleId = mtext.textStyle();
      } else if (entityType === EntityType.MLeader) {
        entity = entityId.safeOpenObject(OpenMode.kForRead, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) styleId = mleader.textStyleId();
      }

      if (!styleId) return null;
      return getEntityHandle(styleId);
    } finally {
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
      if (styleId) deleteObj(styleId);
    }
  }

  setTextStyleByHandle(
    entityId: DrawingModule.OdDbObjectId,
    styleHandle: string,
  ): boolean {
    const module = this.viewer.module;
    const db = this.viewer.database;
    if (!db) return false;

    const styleId = findEntityByHandle(module, db, styleHandle);
    if (!styleId) return false;

    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        text = module.OdDbText.cast(pObj);
        if (text) {
          text.setTextStyle(styleId);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) {
          mtext.setTextStyle(styleId);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MLeader) {
        entity = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) {
          mleader.setOverride(11, true);
          mleader.setTextStyleId(styleId);
          mtext = mleader.mtext();
          if (mtext) {
            mtext.setTextStyle(styleId);
            mleader.setMText(mtext);
          }
          mleader.recomputeBreakPoints();
          recordGraphicsModified(module, entityId);
          return true;
        }
      }

      return false;
    } finally {
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
      deleteObj(styleId);
    }
  }

  /**
   * Set text height on a text entity (OdDbText or OdDbMText).
   */
  setTextHeight(entityId: DrawingModule.OdDbObjectId, height: number): boolean {
    const module = this.viewer.module;
    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;
    let textStyleId: DrawingModule.OdDbObjectId | null = null;
    let drStyleId: DrawingModule.OdDbObjectId | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        text = module.OdDbText.cast(pObj);
        if (text) {
          text.setHeight(height);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) {
          mtext.setTextHeight(height);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MLeader) {
        const db = this.viewer.database;
        if (!db) return false;
        entity = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) {
          textStyleId = mleader.textStyleId();
          drStyleId = switchMLeaderStyle(
            module,
            db,
            mleader,
            textStyleId,
            height,
          );
          mtext = mleader.mtext();
          if (mtext) {
            mtext.setTextHeight(height);
            mleader.setMText(mtext);
          }
          mleader.recomputeBreakPoints();
          recordGraphicsModified(module, entityId);
          return true;
        }
      }

      return false;
    } finally {
      if (drStyleId) deleteObj(drStyleId);
      if (textStyleId) deleteObj(textStyleId);
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
    }
  }

  /**
   * Set text style on a text entity (OdDbText, OdDbMText, or OdDbMLeader).
   * Applies the style's font and, if present, the style's size as height.
   */
  setTextStyle(
    entityId: DrawingModule.OdDbObjectId,
    style: Textstyle,
  ): boolean {
    const module = this.viewer.module;
    const db = this.viewer.database;
    if (!db) return false;

    const entityType = getEntityType(module, entityId);

    let pObj: DrawingModule.OdDbObject | null = null;
    let text: DrawingModule.OdDbText | null = null;
    let mtext: DrawingModule.OdDbMText | null = null;
    let entity: DrawingModule.OdDbObject | null = null;
    let mleader: DrawingModule.OdDbMLeader | null = null;
    let newStyleId: DrawingModule.OdDbObjectId | null = null;
    let drStyleId: DrawingModule.OdDbObjectId | null = null;

    try {
      if (entityType === EntityType.Text) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        text = module.OdDbText.cast(pObj);
        if (text) {
          applyTextStyleToEntity(module, db, text, style);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MText) {
        pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mtext = module.OdDbMText.cast(pObj);
        if (mtext) {
          applyTextStyleToMText(module, db, mtext, style);
          recordGraphicsModified(module, entityId);
          return true;
        }
      } else if (entityType === EntityType.MLeader) {
        entity = entityId.safeOpenObject(OpenMode.kForWrite, false);
        mleader = module.OdDbMLeader.cast(entity);
        if (mleader) {
          newStyleId = ensureTextStyleExists(module, db, style);
          // Keep current height unless the new text style explicitly provides one.
          const newHeight =
            style.size && style.size > 0 ? style.size : mleader.textHeight();
          drStyleId = switchMLeaderStyle(
            module,
            db,
            mleader,
            newStyleId,
            newHeight,
          );
          mtext = mleader.mtext();
          if (mtext) {
            applyTextStyleToMText(module, db, mtext, style);
            mleader.setMText(mtext);
          }
          mleader.recomputeBreakPoints();
          recordGraphicsModified(module, entityId);
          return true;
        }
      }

      return false;
    } finally {
      if (drStyleId) deleteObj(drStyleId);
      if (newStyleId) deleteObj(newStyleId);
      if (mtext) deleteObj(mtext);
      if (text) deleteObj(text);
      if (mleader) deleteObj(mleader);
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
    }
  }
}
